import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Badge, Typography, Space, Modal, Form, Input } from 'antd';
import { partnerAPI, authAPI } from '../services/api';
import { getCompany, clearAuth } from '../services/authToken';
import {
  DashboardOutlined,
  ScheduleOutlined,
  BugOutlined,
  BellOutlined,
  MessageOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { layout } from '../theme/tokens';
import DashboardPage from '../pages/DashboardPage';
import ScheduleListPage from '../pages/ScheduleListPage';
import ScheduleDetailPage from '../pages/ScheduleDetailPage';
import NcpmsPage from '../pages/NcpmsPage';
import NotificationPage from '../pages/NotificationPage';
import InquiryPage from '../pages/InquiryPage';
import UserGuidePage from '../pages/UserGuidePage';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();
  const companyName = getCompany() || 'Partner';
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwForm] = Form.useForm();

  const [unreadCount, setUnreadCount] = useState(0);

  React.useEffect(() => {
    const handleResize = () => { if (window.innerWidth < 768) setCollapsed(true); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 알림함 미읽음 + 문의 답변 미확인 합산 (화면 이동 시 갱신)
  React.useEffect(() => {
    let alive = true;
    Promise.all([
      partnerAPI.notifications().then(r => (r.data || []).filter((n: any) => !n.is_read).length).catch(() => 0),
      partnerAPI.inquirySummary().then(r => r.data?.unread_replies || 0).catch(() => 0),
    ]).then(([n, q]) => { if (alive) setUnreadCount(n + q); });
    return () => { alive = false; };
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwForm.validateFields();
      if (values.new_password !== values.confirm_password) {
        Modal.error({ title: '비밀번호 불일치', content: '새 비밀번호가 일치하지 않습니다.' });
        return;
      }
      await authAPI.changePassword({ current_password: values.current_password, new_password: values.new_password });
      Modal.success({ title: '변경 완료', content: '비밀번호가 변경되었습니다.' });
      setPwModalOpen(false);
      pwForm.resetFields();
    } catch (err: any) {
      Modal.error({ title: '변경 실패', content: err?.response?.data?.detail || '오류가 발생했습니다.' });
    }
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '대시보드',
    },
    {
      key: '/schedules',
      icon: <ScheduleOutlined />,
      label: '시험 일정',
    },
    {
      key: '/ncpms',
      icon: <BugOutlined />,
      label: '병해충 정보',
    },
    {
      key: '/inquiries',
      icon: <MessageOutlined />,
      label: '문의/요청',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: '알림',
    },
    {
      key: '/guide',
      icon: <QuestionCircleOutlined />,
      label: '사용자 가이드',
    },
  ];

  const selectedKey = '/' + location.pathname.split('/')[1];

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <KeyOutlined />,
      label: '비밀번호 변경',
      onClick: () => { pwForm.resetFields(); setPwModalOpen(true); },
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        theme="dark"
        width={layout.sidebarWidth}
        collapsedWidth={layout.sidebarCollapsed}
        style={{
          background: 'linear-gradient(180deg, #451a03 0%, #5c2a0e 50%, #78350f 100%)',
          borderRight: 'none',
          boxShadow: '2px 0 24px rgba(69,26,3,0.15)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          overflow: 'auto',
        }}
      >
        <div style={{
          /* 헤더와 같은 높이 — 사이드바 로고 줄과 본문 헤더의 밑선이 맞는다 */
          height: layout.headerHeight,
          padding: collapsed ? '0 8px' : '0 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          marginBottom: 4,
        }}>
          <div style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 12px rgba(245,158,11,0.4)',
          }}>
            <HomeOutlined style={{ color: '#451a03', fontSize: 16 }} />
          </div>
          {!collapsed && (
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#ffffff', lineHeight: 1.15 }}>SAPPT</div>
              <div style={{ fontSize: 10, color: 'rgba(253,230,138,0.5)', letterSpacing: 1 }}>PARTNER PORTAL</div>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            const item = menuItems.find(m => m.key === key);
            if (item) partnerAPI.logPageView(key, item.label);
          }}
          style={{ border: 'none', background: 'transparent' }}
        />
        {!collapsed && (
          <div style={{
            padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 10, color: 'rgba(253,230,138,0.25)', textAlign: 'center',
            position: 'absolute', bottom: 0, left: 0, right: 0,
          }}>
            SAPPT Partner v1.0 &middot; partner.sappt.co.kr
          </div>
        )}
      </Sider>
      <Layout style={{ marginLeft: collapsed ? layout.sidebarCollapsed : layout.sidebarWidth, transition: 'margin-left 0.2s' }}>
        <Header style={{
          background: '#fff',
          padding: '0 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #fde68a',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          height: layout.headerHeight,
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />
          <Space size={16}>
            <Badge count={unreadCount} size="small">
              <Button
                type="text"
                icon={<BellOutlined style={{ color: unreadCount > 0 ? '#f59e0b' : undefined }} />}
                onClick={() => navigate('/notifications')}
                style={{ fontSize: 16 }}
              />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                />
                <Text strong style={{ color: '#92400e', fontSize: 13 }}>{companyName}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ padding: layout.contentPadding, minHeight: `calc(100vh - ${layout.headerHeight}px - 32px)` }}>
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/schedules" element={<ScheduleListPage />} />
            <Route path="/schedules/:id" element={<ScheduleDetailPage />} />
            <Route path="/ncpms" element={<NcpmsPage />} />
            <Route path="/inquiries" element={<InquiryPage />} />
            <Route path="/notifications" element={<NotificationPage />} />
            <Route path="/guide" element={<UserGuidePage />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Content>
        <Footer style={{
          textAlign: 'center',
          padding: '7px 14px',
          color: '#92400e',
          fontSize: 12,
          background: 'transparent',
        }}>
          SAPPT Partner Portal &copy; 2026
        </Footer>
      </Layout>

      <Modal
        title={<><KeyOutlined style={{ marginRight: 8, color: '#f59e0b' }} />비밀번호 변경</>}
        open={pwModalOpen}
        onCancel={() => setPwModalOpen(false)}
        onOk={handleChangePassword}
        okText="변경"
        width={420}
        maskClosable={false}
      >
        <Form form={pwForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="current_password" label="현재 비밀번호"
            rules={[{ required: true, message: '현재 비밀번호를 입력하세요' }]}>
            <Input.Password placeholder="현재 비밀번호" />
          </Form.Item>
          <Form.Item name="new_password" label="새 비밀번호"
            rules={[{ required: true, message: '새 비밀번호를 입력하세요' }, { min: 4, message: '4자 이상 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 (4자 이상)" />
          </Form.Item>
          <Form.Item name="confirm_password" label="새 비밀번호 확인"
            rules={[{ required: true, message: '비밀번호를 다시 입력하세요' }]}>
            <Input.Password placeholder="새 비밀번호 확인" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MainLayout;
